import { addBreadcrumb, captureMessage, withScope } from '@sentry/react';
import CaptchaEvent from './Captcha.types';

function describeSubmitter(submitter: HTMLElement | null): string | null {
    if (!submitter) {
        return null;
    }

    return (
        submitter.getAttribute('data-testid') ??
        submitter.getAttribute('name') ??
        submitter.tagName.toLowerCase()
    );
}

function recordSubmitBreadcrumb(
    form: HTMLFormElement,
    event: SubmitEvent,
    submitCount: number,
    verified: boolean,
    pending: boolean
): void {
    try {
        const formData = new FormData(form);

        addBreadcrumb({
            category: 'login.submit',
            level: 'info',
            message: `login submit #${submitCount}`,
            data: {
                hasUsername: String(formData.get('username') ?? '').length > 0,
                hasPassword: String(formData.get('password') ?? '').length > 0,
                hasCsrfToken: String(formData.get('csrf_token') ?? '').length > 0,
                hasCaptchaToken: String(formData.get('captcha') ?? '').length > 0,
                submitter: describeSubmitter(event.submitter),
                verified,
                pending
            }
        });
    } catch {
        // Telemetry must never break the submit flow.
    }
}

function reportEmptyCaptchaSubmit(context: Record<string, unknown>): void {
    try {
        withScope((scope) => {
            scope.setLevel('warning');
            scope.setTag('feature', 'login');
            scope.setTag('login_empty_submit', 'true');
            scope.setExtras(context);
            captureMessage('Login submit with empty fields but a solved captcha token');
        });
    } catch {
        // Telemetry must never break the submit flow.
    }
}

function initFormSubmitInterceptor(form: HTMLFormElement, captchaInput: HTMLInputElement): void {
    let verified = false;
    let pending = false;
    let abortController: AbortController | null = null;
    let reported = false;
    let submitCount = 0;

    const reportIfAnomalous = (event: SubmitEvent): void => {
        if (reported) {
            return;
        }

        const formData = new FormData(form);
        const captchaTokenLength = String(formData.get('captcha') ?? '').length;
        const usernameLength = String(formData.get('username') ?? '').length;
        const passwordLength = String(formData.get('password') ?? '').length;

        if (captchaTokenLength === 0 || (usernameLength > 0 && passwordLength > 0)) {
            return;
        }

        reported = true;
        reportEmptyCaptchaSubmit({
            hasCsrfToken: String(formData.get('csrf_token') ?? '').length > 0,
            hasUsername: usernameLength > 0,
            hasPassword: passwordLength > 0,
            hasCaptchaToken: captchaTokenLength > 0,
            verified,
            submitCount,
            submitter: describeSubmitter(event.submitter),
            eventType: event.type,
            visibilityState: document.visibilityState,
            userAgent: navigator.userAgent
        });
    };

    form.addEventListener(
        'submit',
        (e: SubmitEvent) => {
            submitCount += 1;
            recordSubmitBreadcrumb(form, e, submitCount, verified, pending);
            reportIfAnomalous(e);

            if (verified) {
                return;
            }

            e.preventDefault();
            e.stopImmediatePropagation();

            if (pending) {
                return;
            }

            pending = true;
            abortController = new AbortController();
            const { signal } = abortController;

            const resetPending = (): void => {
                pending = false;
                abortController?.abort();
                abortController = null;
            };

            form.addEventListener(
                CaptchaEvent.SUCCESS,
                () => {
                    verified = true;
                    resetPending();
                    form.requestSubmit();
                },
                { once: true, signal }
            );

            form.addEventListener(CaptchaEvent.ERROR, resetPending, { once: true, signal });
            form.addEventListener(CaptchaEvent.EXPIRE, resetPending, { once: true, signal });

            captchaInput.dispatchEvent(new CustomEvent(CaptchaEvent.EXECUTE));
        },
        { capture: true }
    );
}

function initSocialLoginInterceptor(form: HTMLFormElement, captchaInput: HTMLInputElement): void {
    form.querySelectorAll<HTMLAnchorElement>('[data-role="social-login"]').forEach((anchor) => {
        const originalHref = anchor.href;

        anchor.addEventListener('click', (e: MouseEvent) => {
            e.preventDefault();

            const abortController = new AbortController();
            const { signal } = abortController;

            form.addEventListener(
                CaptchaEvent.SUCCESS,
                (event: Event) => {
                    const { token } = (event as CustomEvent<{ token: string }>).detail;
                    abortController.abort();

                    const url = new URL(originalHref);
                    url.searchParams.set('token', token);
                    window.location.href = url.toString();
                },
                { once: true, signal }
            );

            form.addEventListener(CaptchaEvent.ERROR, () => abortController.abort(), {
                once: true,
                signal
            });
            form.addEventListener(CaptchaEvent.EXPIRE, () => abortController.abort(), {
                once: true,
                signal
            });

            captchaInput.dispatchEvent(new CustomEvent(CaptchaEvent.EXECUTE));
        });
    });
}

export default function initCaptchaInterceptor(): void {
    document.querySelectorAll<HTMLFormElement>('form').forEach((form) => {
        if (
            form.closest('dialog') ||
            form.getAttribute('is') === 'nh-login-form' ||
            form.getAttribute('is') === 'landlord-registration-form'
        ) {
            return;
        }

        const captchaInput = form.querySelector<HTMLInputElement>('[data-role="cf-turnstile"]');
        if (!captchaInput) {
            return;
        }

        if (form.dataset.captchaInterceptorInitialized === 'true') {
            return;
        }

        form.dataset.captchaInterceptorInitialized = 'true';

        initFormSubmitInterceptor(form, captchaInput);
        initSocialLoginInterceptor(form, captchaInput);
    });
}
